"use client";

import Image from "next/image";
import Link from "../../../components/LocaleLink";
import { useLocale } from "../../../components/LocaleProvider";
import { localizePath, type Locale } from "../../../lib/i18n/config";

const PHONE_STANMORE = "02033 016323";
const PHONE_WALTON = "01932 988460";

// ——— Secțiuni Dental ———
const sections = [
  {
    id: "full-mouth-implants",
    title: "Full Mouth Dental Implants",
    subtitle:
      "Probably the best provider of full mouth dental implants in UK.",
    desc: "Fixed full-arch ‘all-on-X’ solutions for patients with failing or missing teeth. At EgoDent, full-mouth implant cases are planned digitally with CBCT, guided surgery and close collaboration with our in-house lab, aiming for stable function, natural aesthetics and long-term comfort.",
    image: "/dental/sections/full-mouth-implants.jpg",
  },
    {
    id: "orthodontics",
    title: "Orthodontics & Aligners",
    subtitle: "Discreet solutions for straighter teeth and a balanced bite.",
    desc: "Specialist orthodontic care for children, teenagers and adults — using fixed braces or clear aligners, planned for natural aesthetics, comfortable function and long-term stability. Ideal for crowding, spacing, bite correction, relapse cases, and pre-restorative/pre-implant preparation.",
    image: "/dental/sections/orthodontics.jpg",
  },
  {
    id: "implants",
    title: "Dental Implants",
    subtitle:
      "Replace missing teeth permanently with precision-engineered titanium implants.",
    desc: "At EgoDent, our implant specialists provide cutting-edge solutions for single, multiple, or full-arch restorations. We use world-leading implant systems, 3D-guided surgery, and same-day restoration protocols for maximum precision and comfort. Includes digital planning, surgical placement, and prosthetic crown connection.",
    image: "/dental/sections/implants.jpg",
  },
  {
    id: "cosmetics",
    title: "Cosmetic Dentistry",
    subtitle:
      "Transform your smile with advanced aesthetic treatments tailored to perfection.",
    desc: "Our cosmetic experts offer a full range of smile design options — including veneers, whitening, and composite bonding. Each treatment plan is customised using 3D mockups and high-precision digital design for a natural and confident look.",
    image: "/dental/sections/cosmetics.jpg",
  },
  {
    id: "restoration",
    title: "Crowns & Restorations",
    subtitle:
      "Restore strength and beauty to damaged teeth with premium materials.",
    desc: "Using CAD/CAM and in-house milling, we create zirconia and E-max restorations that perfectly match your smile. Every restoration is designed and fitted with microscopic accuracy for longevity and seamless integration.",
    image: "/dental/sections/restoration.jpg",
  },

  {
    id: "periodontal",
    title: "Gum Health & Periodontics",
    subtitle: "Advanced gum treatments for long-term oral health and stability.",
    desc: "Our periodontal specialists provide deep cleaning, regeneration, and soft tissue surgery to restore gum health. We focus on minimally invasive approaches for both prevention and reconstruction.",
    image: "/dental/sections/periodontal.jpg",
  },
  {
    id: "general",
    title: "General Dentistry",
    subtitle:
      "Comprehensive care for all ages — prevention, diagnosis, and treatment.",
    desc: "From check-ups and fillings to emergency care, EgoDent provides complete dental care for every patient. Using modern diagnostics, we ensure long-term oral health with comfort and transparency.",
    image: "/dental/sections/general.jpg",
  },
];

const sectionsRo: Record<string, { title: string; subtitle: string; desc: string }> = {
  "full-mouth-implants": {
    title: "Reabilitare completă pe implanturi",
    subtitle:
      "Probabil cel mai bun furnizor de implanturi dentare pe arcadă completă din Marea Britanie.",
    desc: "Soluții fixe pe arcadă completă, de tip „all-on-X”, pentru pacienții cu dinți compromiși sau lipsă. La EgoDent, cazurile de reabilitare completă pe implanturi sunt planificate digital cu CBCT, chirurgie ghidată și în strânsă colaborare cu laboratorul nostru propriu, cu scopul unei funcții stabile, al unei estetici naturale și al confortului pe termen lung.",
  },
  orthodontics: {
    title: "Ortodonție și alignere",
    subtitle: "Soluții discrete pentru dinți mai drepți și o ocluzie echilibrată.",
    desc: "Îngrijire ortodontică de specialitate pentru copii, adolescenți și adulți — cu aparat dentar fix sau alignere transparente, planificate pentru o estetică naturală, o funcție confortabilă și stabilitate pe termen lung. Ideală pentru înghesuire, spațieri, corectarea ocluziei, cazuri de recidivă și pregătirea pre-restaurativă sau pre-implantară.",
  },
  implants: {
    title: "Implanturi dentare",
    subtitle:
      "Înlocuiți definitiv dinții lipsă cu implanturi de titan realizate cu precizie.",
    desc: "La EgoDent, specialiștii noștri în implantologie oferă soluții de ultimă generație pentru restaurări unice, multiple sau pe arcadă completă. Folosim sisteme de implanturi de top la nivel mondial, chirurgie ghidată 3D și protocoale de restaurare în aceeași zi, pentru precizie și confort maxim. Include planificare digitală, inserarea chirurgicală și conectarea coroanei protetice.",
  },
  cosmetics: {
    title: "Stomatologie estetică",
    subtitle:
      "Transformați-vă zâmbetul prin tratamente estetice avansate, adaptate perfect nevoilor dumneavoastră.",
    desc: "Specialiștii noștri în estetică dentară oferă o gamă completă de opțiuni de design al zâmbetului — inclusiv fațete, albire și bonding cu rășină compozită. Fiecare plan de tratament este personalizat folosind machete 3D și design digital de înaltă precizie, pentru un aspect natural și plin de încredere.",
  },
  restoration: {
    title: "Coroane și restaurări",
    subtitle:
      "Redați rezistența și frumusețea dinților afectați, folosind materiale premium.",
    desc: "Folosind CAD/CAM și frezare în laboratorul propriu, realizăm restaurări din zirconiu și E-max care se potrivesc perfect zâmbetului dumneavoastră. Fiecare restaurare este proiectată și adaptată cu acuratețe microscopică, pentru longevitate și integrare impecabilă.",
  },
  periodontal: {
    title: "Sănătatea gingiilor și parodontologie",
    subtitle:
      "Tratamente avansate ale gingiilor pentru sănătate orală și stabilitate pe termen lung.",
    desc: "Specialiștii noștri în parodontologie oferă detartraj în profunzime, tehnici de regenerare și chirurgie a țesuturilor moi pentru a restabili sănătatea gingiilor. Ne concentrăm pe abordări minim invazive, atât pentru prevenție, cât și pentru reconstrucție.",
  },
  general: {
    title: "Stomatologie generală",
    subtitle:
      "Îngrijire completă pentru toate vârstele — prevenție, diagnostic și tratament.",
    desc: "De la controale și obturații până la îngrijire de urgență, EgoDent oferă îngrijire stomatologică completă pentru fiecare pacient. Prin diagnostic modern, asigurăm sănătate orală pe termen lung, cu confort și transparență.",
  },
};

const copy = {
  en: {
    h1: "Dental Services at EgoDent",
    intro:
      "Explore EgoDent’s complete range of advanced dental treatments — designed for comfort, precision and long-term results. From implants and cosmetic dentistry to orthodontics, periodontics and general care.",
    book: "Book Dental Appointment",
    callStanmoreFull: "Call Stanmore:",
    callWaltonFull: "Call Walton-on-Thames:",
    sameDay: "Same-day appointments may be available depending on clinician schedule.",
    altSuffix: "EgoDent Dental Clinic",
    bookSection: "Book",
    callStanmore: "Call Stanmore",
    callWalton: "Call Walton-on-Thames",
    learnMore: "Learn more",
    footerCall: "For dental appointments call",
  },
  ro: {
    h1: "Servicii stomatologice la EgoDent",
    intro:
      "Descoperiți gama completă de tratamente stomatologice avansate de la EgoDent — concepute pentru confort, precizie și rezultate de durată. De la implanturi și stomatologie estetică până la ortodonție, parodontologie și îngrijire generală.",
    book: "Programare stomatologică",
    callStanmoreFull: "Sună Stanmore:",
    callWaltonFull: "Sună Walton-on-Thames:",
    sameDay: "Programări în aceeași zi pot fi disponibile, în funcție de programul medicilor.",
    altSuffix: "Clinica Stomatologică EgoDent",
    bookSection: "Programare:",
    callStanmore: "Sună Stanmore",
    callWalton: "Sună Walton-on-Thames",
    learnMore: "Aflați mai mult",
    footerCall: "Pentru programări stomatologice sunați la",
  },
} as const;

// ——— JSON-LD Service per secțiune (cu rating) ———
const serviceJsonLd = (s: (typeof sections)[number], locale: Locale) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: `EgoDent — ${s.title}`,
  serviceType: s.title,
  description: s.desc,
  category: "Dentistry",
  areaServed: [
    { "@type": "City", name: "Stanmore" },
    { "@type": "City", name: "Walton-on-Thames" },
  ],
  provider: {
    "@type": "Dentist",
    name: "EgoDent Dental & Medical Clinics",
    telephone: ["+44 2033016323", "+44 1932988460"],
    address: [
      {
        "@type": "PostalAddress",
        addressLocality: "Stanmore",
        addressCountry: "UK",
      },
      {
        "@type": "PostalAddress",
        addressLocality: "Walton-on-Thames",
        addressCountry: "UK",
      },
    ],
  },
  image: s.image,
  url: `https://egodent.co.uk${localizePath(locale, "/dental")}#${s.id}`,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    bestRating: "5",
    reviewCount: "1000",
  },
});

// ——— JSON-LD general Dentist ———
const clinicJsonLd = {
  "@context": "https://schema.org",
  "@type": "Dentist",
  name: "EgoDent Dental & Medical Clinics",
  url: "https://egodent.co.uk/dental",
  telephone: ["+44 2033016323", "+44 1932988460"],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    bestRating: "5",
    reviewCount: "1000",
  },
  department: [
    {
      "@type": "Dentist",
      name: "EgoDent Stanmore",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Stanmore",
        addressCountry: "UK",
      },
      telephone: "+44 2033016323",
    },
    {
      "@type": "Dentist",
      name: "EgoDent Walton-on-Thames",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Walton-on-Thames",
        addressCountry: "UK",
      },
      telephone: "+44 1932988460",
    },
  ],
};

export default function DentalPageClient() {
  const locale = useLocale();
  const t = copy[locale];
  const localized = sections.map((s) =>
    locale === "ro" ? { ...s, ...sectionsRo[s.id] } : s
  );

  return (
    <>
      {/* HERO – stil identic cu Medical */}
      <section className="container-px mx-auto py-16 text-center relative overflow-hidden bg-black text-white">
        {/* Glow verde sub hero */}
        <div className="pointer-events-none absolute -bottom-10 left-1/2 -translate-x-1/2 h-32 w-[70%] rounded-full blur-3xl bg-[#27E8A7]/25" />

        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white">
          {t.h1}
        </h1>
        <div className="mt-3 h-px w-20 mx-auto bg-gradient-to-r from-transparent via-[#27E8A7] to-transparent" />
        <p className="mt-4 text-white/70 text-base md:text-lg max-w-3xl mx-auto">
          {t.intro}
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link
            href="/book-online"
            data-cta="book-dental-hero"
            className="btn btn-primary"
          >
            {t.book}
          </Link>
          <a href="tel:+442033016323" className="btn btn-outline">
            {t.callStanmoreFull} {PHONE_STANMORE}
          </a>
          <a href="tel:+441932988460" className="btn btn-outline">
            {t.callWaltonFull} {PHONE_WALTON}
          </a>
        </div>

        <p className="mt-4 text-xs md:text-sm text-white/55">
          {t.sameDay}
        </p>
      </section>

      {/* SECȚIUNI – card cu stil ca la Medical, peste imagine */}
      {localized.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className="relative overflow-hidden bg-black pb-10 md:pb-0"
        >
          {/* Imagine full-width */}
          <div className="relative h-[480px] md:h-[580px] w-full">
            <Image
              src={s.image}
              alt={`${s.title} — ${t.altSuffix}`}
              fill
              loading="lazy"
              placeholder="blur"
              blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAoMBgKGGtW0AAAAASUVORK5CYII="
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 h-28 w-[80%] rounded-full blur-3xl bg-[#27E8A7]/25" />
          </div>

          {/* Card text – stil Medical */}
          <div
            className="md:absolute inset-0 flex flex-col justify-center items-start px-6 md:px-20 text-left py-8 md:py-0"
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <div className="max-w-3xl bg-black/70 border border-[#27E8A7]/40 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-[0_0_20px_rgba(39,232,167,0.15)] relative">
              <h2 className="text-2xl md:text-3xl font-semibold text-white">
                {s.title}
              </h2>
              <p className="mt-2 text-sm uppercase tracking-[0.16em] text-[#27E8A7]/80">
                {s.subtitle}
              </p>
              <p className="mt-4 text-white/75 text-sm md:text-base leading-relaxed">
                {s.desc}
              </p>

              <div className="mt-6 flex flex-wrap gap-3 relative z-10">
                <Link
                  href="/book-online"
                  data-cta={`book-dental-${s.id}`}
                  className="btn btn-primary text-xs md:text-sm"
                >
                  {t.bookSection} {s.title}
                </Link>
                <a
                  href={`tel:+44${PHONE_STANMORE.replace(/\s/g, "")}`}
                  className="btn btn-outline text-xs md:text-sm"
                >
                  {t.callStanmore}
                </a>
                <a
                  href={`tel:+44${PHONE_WALTON.replace(/\s/g, "")}`}
                  className="btn btn-outline text-xs md:text-sm"
                >
                  {t.callWalton}
                </a>
                <Link
                  href={`/dental/${s.id}`}
                  className="btn btn-outline text-xs md:text-sm"
                >
                  {t.learnMore}
                </Link>
              </div>
            </div>
          </div>

          {/* JSON-LD pentru secțiune */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(serviceJsonLd(s, locale)),
            }}
          />
        </section>
      ))}

      {/* Footer CTA – identic cu celelalte pagini */}
      <section className="container-px mx-auto my-16">
        <div className="rounded-2xl border border-[#27E8A7]/40 bg-black/80 backdrop-blur-sm px-6 py-5 text-center shadow-[0_0_25px_rgba(39,232,167,0.15)]">
          <p className="text-sm text-white">
            {t.footerCall}{" "}
            <span className="text-[#27E8A7]">
              Stanmore:{" "}
              <a href="tel:+442033016323" className="underline">
                02033 016323
              </a>
            </span>{" "}
            &nbsp;•&nbsp;
            <span className="text-[#27E8A7]">
              Walton-on-Thames:{" "}
              <a href="tel:+441932988460" className="underline">
                01932 988460
              </a>
            </span>
          </p>
        </div>
      </section>

      {/* JSON-LD general Dentist */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(clinicJsonLd) }}
      />
    </>
  );
}
